import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Compass, FileText, Lightbulb, Target } from "lucide-react";

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-50px" },
  transition: { duration: 0.6, ease: [0.16, 0.84, 0.44, 1] },
};

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative kk-grain">
        <div className="kk-container pt-20 pb-24 lg:pt-28 lg:pb-32 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <motion.div {...fadeUp}>
              <div className="kk-overline mb-6">Digitaler Erstberater &nbsp;·&nbsp; Standortbestimmung</div>
              <h1 className="text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] font-bold text-[var(--kk-anthracite)] tracking-tight">
                Finden Sie heraus, <br className="hidden sm:block" />
                wo Ihre <span className="relative inline-block">
                  <span className="relative z-10">Zeit</span>
                  <span className="absolute inset-x-0 bottom-1 h-3 bg-[var(--kk-bluegreen)]/15 z-0" />
                </span> wirklich bleibt.
              </h1>
              <p className="mt-8 text-lg sm:text-xl text-[var(--kk-anthracite)]/75 leading-relaxed max-w-2xl">
                Der KI-Kompass führt Sie in 15 ruhigen Fragen durch Ihren
                Arbeitsalltag – und zeigt am Ende, wo Sie heute am stärksten
                entlastet werden können. Kein technisches Tool. Ein digitaler
                Brückenbauer.
              </p>
              <div className="mt-10 flex flex-wrap gap-3">
                <Link to="/kompass" className="kk-btn-primary" data-testid="hero-start-btn">
                  Standortbestimmung starten
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/methode" className="kk-btn-ghost" data-testid="hero-methode-btn">
                  Methode kennenlernen
                </Link>
              </div>
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs uppercase tracking-[0.18em] text-[var(--kk-anthracite)]/55">
                <span>~ 7 Minuten</span>
                <span className="w-1 h-1 rounded-full bg-[var(--kk-anthracite)]/30" />
                <span>Keine Anmeldung</span>
                <span className="w-1 h-1 rounded-full bg-[var(--kk-anthracite)]/30" />
                <span>Kurzbericht inklusive</span>
              </div>
            </motion.div>
          </div>

          {/* Right – stylized compass card */}
          <div className="lg:col-span-5">
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, ease: [0.16, 0.84, 0.44, 1], delay: 0.15 }}
              className="relative aspect-square max-w-md mx-auto bg-white border border-[hsl(var(--border))] kk-crosshair overflow-hidden"
              data-testid="hero-compass-visual"
            >
              <div className="absolute inset-6 flex flex-col">
                <div className="flex items-start justify-between">
                  <div className="kk-overline">KI-Kompass</div>
                  <Compass className="w-5 h-5 text-[var(--kk-bluegreen)]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 flex items-center justify-center">
                  <div className="relative w-56 h-56">
                    <div className="absolute inset-0 rounded-full border border-[var(--kk-anthracite)]/15" />
                    <div className="absolute inset-6 rounded-full border border-[var(--kk-anthracite)]/10" />
                    <div className="absolute inset-12 rounded-full border border-[var(--kk-anthracite)]/8" />
                    {["N", "O", "S", "W"].map((d, i) => (
                      <div
                        key={d}
                        className="absolute text-[10px] tracking-[0.3em] font-semibold text-[var(--kk-bluegreen)]"
                        style={{
                          top: i === 0 ? "-8px" : i === 2 ? "auto" : "50%",
                          bottom: i === 2 ? "-8px" : "auto",
                          left: i === 3 ? "-12px" : i === 1 ? "auto" : "50%",
                          right: i === 1 ? "-12px" : "auto",
                          transform: i % 2 === 0 ? "translateX(-50%)" : "translateY(-50%)",
                        }}
                      >
                        {d}
                      </div>
                    ))}
                    <motion.div
                      animate={{ rotate: [0, 360] }}
                      transition={{ duration: 32, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-0 flex items-center justify-center"
                    >
                      <div className="w-px h-44 bg-gradient-to-b from-[var(--kk-bluegreen)] via-[var(--kk-bluegreen)] to-transparent" />
                    </motion.div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-[var(--kk-anthracite)]" />
                    </div>
                  </div>
                </div>
                <div className="flex items-end justify-between text-[10px] uppercase tracking-[0.22em] text-[var(--kk-anthracite)]/50">
                  <span>15 Fragen</span>
                  <span>5 Scores</span>
                  <span>1 Pilot</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="border-t border-[hsl(var(--border))] bg-white">
        <div className="kk-container py-20 lg:py-24">
          <motion.div {...fadeUp} className="max-w-2xl mb-14">
            <div className="kk-overline mb-4">Was Sie am Ende erhalten</div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[var(--kk-anthracite)]">
              Eine klare Standortbestimmung – kein 80-seitiges Konzept.
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-px bg-[hsl(var(--border))]">
            {[
              {
                icon: Target,
                over: "01",
                title: "Fünf Handlungsfelder",
                body: "Zeitfresser, Wissenssicherung, wiederkehrende Aufgaben, Entlastungspotenziale und Handlungsfähigkeit – auf einen Blick.",
              },
              {
                icon: FileText,
                over: "02",
                title: "Individueller Kurzbericht",
                body: "Eine schriftliche Einordnung Ihrer Situation. Ruhig formuliert. Auf Sie zugeschnitten.",
              },
              {
                icon: Lightbulb,
                over: "03",
                title: "Pilotvorschlag",
                body: "Ein konkreter Vorschlag, womit Sie in 14 Tagen praktische Entlastung erleben können.",
              },
            ].map((card) => (
              <motion.div
                key={card.over}
                {...fadeUp}
                className="bg-white p-8 lg:p-10"
                data-testid={`benefit-${card.over}`}
              >
                <div className="flex items-start justify-between">
                  <span className="kk-numeric text-3xl text-[var(--kk-bluegreen)]/40">{card.over}</span>
                  <card.icon className="w-5 h-5 text-[var(--kk-bluegreen)]" strokeWidth={1.5} />
                </div>
                <h3 className="mt-12 text-xl font-semibold tracking-tight text-[var(--kk-anthracite)]">
                  {card.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-[var(--kk-anthracite)]/70">
                  {card.body}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO IT IS FOR */}
      <section className="bg-[var(--kk-offwhite)]">
        <div className="kk-container py-20 lg:py-24 grid lg:grid-cols-12 gap-10">
          <motion.div {...fadeUp} className="lg:col-span-5">
            <div className="kk-overline mb-4">Für wen</div>
            <h2 className="text-3xl sm:text-4xl tracking-tight font-bold text-[var(--kk-anthracite)]">
              Organisationen, die ihre Wirkung schützen wollen.
            </h2>
            <p className="mt-6 text-base text-[var(--kk-anthracite)]/70 leading-relaxed max-w-md">
              Der KI-Kompass entstand aus der Beratungspraxis mit Kommunen,
              Stadtwerken und mittelständischen Unternehmen. Er passt überall
              dort, wo Menschen kompetent arbeiten – und ihre Zeit dort
              einsetzen wollen, wo es wirklich zählt.
            </p>
          </motion.div>
          <motion.div {...fadeUp} className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 gap-px bg-[hsl(var(--border))]">
              {[
                ["Kommunen & Verwaltung", "Bürgerorientierte Prozesse, Aktenarbeit, Routineanfragen"],
                ["Stadtwerke & Versorger", "Wiederkehrende Auskünfte, Tarifkommunikation, Dokumentation"],
                ["Kläranlagen", "Wartungswissen, Protokolle, Schichtübergaben"],
                ["Handwerk & Mittelstand", "Angebotsvorbereitung, Mailkorrespondenz, Wissenstransfer"],
              ].map(([t, d]) => (
                <div key={t} className="bg-white p-7" data-testid={`audience-${t}`}>
                  <div className="text-base font-semibold text-[var(--kk-anthracite)] tracking-tight">{t}</div>
                  <div className="mt-2 text-sm text-[var(--kk-anthracite)]/60 leading-relaxed">{d}</div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-[var(--kk-anthracite)] text-white">
        <div className="kk-container py-16 lg:py-20 flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
          <div>
            <div className="text-xs uppercase tracking-[0.22em] text-white/50 mb-3">Bereit für den ersten Schritt</div>
            <h3 className="text-2xl sm:text-3xl font-bold tracking-tight max-w-2xl" style={{ fontFamily: "Cabinet Grotesk" }}>
              In sieben Minuten zu Ihrer ersten Einschätzung – schriftlich, klar, ohne Buzzwords.
            </h3>
          </div>
          <Link to="/kompass" className="kk-btn-primary !bg-white !text-[var(--kk-anthracite)] hover:!bg-[var(--kk-offwhite)]" data-testid="footer-cta-btn">
            Jetzt Kompass starten
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
